import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Camera, Plus, Trash2, Sparkles,
  Clock, Users, ChevronDown, ChevronUp, Loader2, Check, X
} from 'lucide-react';
import { foodCommunityService, Ingredient } from '../services/foodCommunityService';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

const DIET_TAGS = [
  { value: 'HIGH_PROTEIN', label: '#highprotein' },
  { value: 'LOW_CALORIE', label: '#lowcalorie' },
  { value: 'LOW_CARB', label: '#lowcarb' },
  { value: 'VEGAN', label: '#vegan' },
  { value: 'VEGETARIAN', label: '#vegetarian' },
  { value: 'GLUTEN_FREE', label: '#faragluten' },
  { value: 'DAIRY_FREE', label: '#faralactate' },
  { value: 'ANTI_INFLAMMATORY', label: '#antiinflamator' },
  { value: 'WEIGHT_LOSS', label: '#slabit' },
  { value: 'MUSCLE_GAIN', label: '#masamusculara' },
  { value: 'KETO', label: '#keto' },
  { value: 'MEDITERRANEAN', label: '#mediteranean' },
  { value: 'HIGH_FIBER', label: '#highfiber' },
  { value: 'LOW_FAT', label: '#lowfat' },
  { value: 'BALANCED', label: '#echilibrat' },
];

const MEAL_TYPES = [
  { value: 'breakfast', label: 'Mic dejun' },
  { value: 'lunch', label: 'Pranz' },
  { value: 'dinner', label: 'Cina' },
  { value: 'snack', label: 'Gustare' },
];

const EditRecipePostPage: React.FC = () => {
  const navigate = useNavigate();
  const { postId } = useParams<{ postId: string }>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(true);
  const [imageUrl, setImageUrl] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [mealType, setMealType] = useState('');
  const [prepTime, setPrepTime] = useState('');
  const [servings, setServings] = useState('1');

  const [ingredients, setIngredients] = useState<Ingredient[]>([
    { name: '', amount: '', unit: 'g' }
  ]);
  const [instructions, setInstructions] = useState('');

  const [calories, setCalories] = useState('');
  const [protein, setProtein] = useState('');
  const [carbs, setCarbs] = useState('');
  const [fats, setFats] = useState('');

  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [customTag, setCustomTag] = useState('');
  const [customTags, setCustomTags] = useState<string[]>([]);

  const [showNutrition, setShowNutrition] = useState(true);
  const [showIngredients, setShowIngredients] = useState(true);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Load existing post data
  useEffect(() => {
    if (!postId) return;
    loadPost();
  }, [postId]);

  const loadPost = async () => {
    setLoading(true);
    try {
      const post = await foodCommunityService.getPost(postId!);
      setTitle(post.title || '');
      setDescription(post.description || '');
      setMealType(post.mealType || '');
      setPrepTime(post.prepTime ? String(post.prepTime) : '');
      setServings(post.servings ? String(post.servings) : '1');
      setImageUrl(post.imageUrl || '');
      setImagePreview(post.imageUrl || '');
      setInstructions(post.instructions || '');
      setCalories(post.calories ? String(Math.round(post.calories)) : '');
      setProtein(post.protein ? String(Math.round(post.protein)) : '');
      setCarbs(post.carbs ? String(Math.round(post.carbs)) : '');
      setFats(post.fats ? String(Math.round(post.fats)) : '');
      setSelectedTags(post.tags || []);
      setCustomTags(post.customTags || []);
      if (post.ingredients && post.ingredients.length > 0) {
        setIngredients(post.ingredients);
      }
    } catch {
      setError('Nu am putut incarca postarea.');
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target?.result as string);
    reader.readAsDataURL(file);

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        setImageUrl(data.url || data.imageUrl || '');
      }
    } catch {}
    finally {
      setUploadingImage(false);
    }
  };

  const handleAddIngredient = () => {
    setIngredients(prev => [...prev, { name: '', amount: '', unit: 'g' }]);
  };

  const handleRemoveIngredient = (idx: number) => {
    setIngredients(prev => prev.filter((_, i) => i !== idx));
  };

  const handleIngredientChange = (idx: number, field: keyof Ingredient, value: string) => {
    setIngredients(prev => prev.map((ing, i) => i === idx ? { ...ing, [field]: value } : ing));
  };

  const handleTagToggle = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const handleAddCustomTag = () => {
    const tag = customTag.trim().replace(/^#/, '').toLowerCase();
    if (tag && !customTags.includes(tag)) {
      setCustomTags(prev => [...prev, tag]);
      setCustomTag('');
    }
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError('Titlul retetei este obligatoriu');
      return;
    }
    setError('');
    setSubmitting(true);

    try {
      const validIngredients = ingredients.filter(i => i.name.trim());
      await foodCommunityService.updatePost(postId!, {
        title: title.trim(),
        description: description.trim() || undefined,
        imageUrl: imageUrl || undefined,
        prepTime: prepTime ? parseInt(prepTime) : undefined,
        servings: servings ? parseInt(servings) : 1,
        calories: calories ? parseFloat(calories) : undefined,
        protein: protein ? parseFloat(protein) : undefined,
        carbs: carbs ? parseFloat(carbs) : undefined,
        fats: fats ? parseFloat(fats) : undefined,
        ingredients: validIngredients.length > 0 ? validIngredients : undefined,
        instructions: instructions.trim() || undefined,
        tags: selectedTags,
        customTags,
        mealType: mealType || undefined,
      } as any);
      navigate(`/food-community/post/${postId}`, { replace: true });
    } catch (err: any) {
      setError(err?.response?.data?.error || 'A aparut o eroare. Incearca din nou.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-green-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-30">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft size={18} className="text-gray-600" />
            </button>
            <h1 className="font-bold text-gray-900 text-lg">Editeaza reteta</h1>
          </div>
          <button
            onClick={handleSubmit}
            disabled={!title.trim() || submitting}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl font-semibold text-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
            Salveaza
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4 pb-24 space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
            {error}
          </div>
        )}

        {/* Image */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          {imagePreview ? (
            <label htmlFor="edit-gallery-input" className="relative block cursor-pointer">
              <img
                src={imagePreview}
                alt="Preview"
                className="w-full object-cover"
                style={{ maxHeight: 280 }}
              />
              <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 hover:opacity-100 active:opacity-100 transition-opacity">
                <div className="bg-white/90 rounded-full px-4 py-2 flex items-center gap-2 text-sm font-medium text-gray-700">
                  <Camera size={16} />
                  Schimba poza
                </div>
              </div>
              {uploadingImage && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <Loader2 size={28} className="animate-spin text-white" />
                </div>
              )}
            </label>
          ) : (
            <label
              htmlFor="edit-gallery-input"
              className="h-44 flex flex-col items-center justify-center gap-2 text-gray-400 bg-gray-50 hover:bg-gray-100 active:bg-gray-100 transition-colors cursor-pointer"
            >
              <Camera size={32} className="text-gray-300" />
              <span className="text-sm font-medium">Adauga poza</span>
              <span className="text-xs text-gray-400">JPG, PNG, WebP, GIF acceptate</span>
            </label>
          )}
          {/* No capture → iOS/Android shows photo library picker */}
          <input
            id="edit-gallery-input"
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,image/heic,image/heif"
            className="hidden"
            onChange={handleImageSelect}
          />
        </div>

        {/* Basic Info */}
        <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
              Titlu reteta *
            </label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="ex: Bowl cu somon si avocado"
              className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
              Descriere
            </label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Descrie pe scurt reteta ta..."
              rows={3}
              className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
                Tip masa
              </label>
              <select
                value={mealType}
                onChange={e => setMealType(e.target.value)}
                className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="">Selecteaza</option>
                {MEAL_TYPES.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
                <Clock size={11} className="inline mr-1" />Timp (min)
              </label>
              <input
                type="number"
                value={prepTime}
                onChange={e => setPrepTime(e.target.value)}
                placeholder="15"
                min="1"
                className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
                <Users size={11} className="inline mr-1" />Portii
              </label>
              <input
                type="number"
                value={servings}
                onChange={e => setServings(e.target.value)}
                placeholder="1"
                min="1"
                className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>
        </div>

        {/* Nutrition */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <button
            onClick={() => setShowNutrition(!showNutrition)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-green-500" />
              <span className="font-semibold text-gray-800 text-sm">Valori nutritionale</span>
            </div>
            {showNutrition ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
          </button>

          {showNutrition && (
            <div className="px-4 pb-4 grid grid-cols-2 gap-3">
              {[
                { label: 'Calorii (kcal)', value: calories, setter: setCalories, color: 'text-orange-500' },
                { label: 'Proteine (g)', value: protein, setter: setProtein, color: 'text-green-600' },
                { label: 'Carbohidrati (g)', value: carbs, setter: setCarbs, color: 'text-blue-600' },
                { label: 'Grasimi (g)', value: fats, setter: setFats, color: 'text-amber-600' },
              ].map(({ label, value, setter, color }) => (
                <div key={label}>
                  <label className={`text-xs font-semibold ${color} mb-1 block`}>{label}</label>
                  <input
                    type="number"
                    value={value}
                    onChange={e => setter(e.target.value)}
                    placeholder="0"
                    min="0"
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Ingredients */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <button
            onClick={() => setShowIngredients(!showIngredients)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
          >
            <span className="font-semibold text-gray-800 text-sm">Ingrediente ({ingredients.filter(i => i.name).length})</span>
            {showIngredients ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
          </button>

          {showIngredients && (
            <div className="px-4 pb-4 space-y-2">
              {ingredients.map((ing, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={ing.amount}
                    onChange={e => handleIngredientChange(idx, 'amount', e.target.value)}
                    placeholder="100"
                    className="w-16 px-2 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-center outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <select
                    value={ing.unit || 'g'}
                    onChange={e => handleIngredientChange(idx, 'unit', e.target.value)}
                    className="w-16 px-1 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs outline-none focus:ring-2 focus:ring-green-500"
                  >
                    {['g', 'kg', 'ml', 'l', 'buc', 'lingura', 'lingurita', 'cana'].map(u => (
                      <option key={u} value={u}>{u}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={ing.name}
                    onChange={e => handleIngredientChange(idx, 'name', e.target.value)}
                    placeholder="Ingredient"
                    className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-green-500"
                  />
                  {ingredients.length > 1 && (
                    <button
                      onClick={() => handleRemoveIngredient(idx)}
                      className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={handleAddIngredient}
                className="flex items-center gap-1.5 text-sm text-green-600 font-medium hover:text-green-700 mt-1"
              >
                <Plus size={15} />
                Adauga ingredient
              </button>
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
            Mod de preparare
          </label>
          <textarea
            value={instructions}
            onChange={e => setInstructions(e.target.value)}
            placeholder="Descrie pasii de preparare..."
            rows={5}
            className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-green-500 resize-none"
          />
        </div>

        {/* Tags */}
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 block">
            Taguri dieta
          </label>
          <div className="flex flex-wrap gap-2 mb-3">
            {DIET_TAGS.map(tag => (
              <button
                key={tag.value}
                onClick={() => handleTagToggle(tag.value)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  selectedTags.includes(tag.value)
                    ? 'bg-green-600 text-white border-green-600'
                    : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-green-300'
                }`}
              >
                {tag.label}
              </button>
            ))}
          </div>

          {/* Custom tags */}
          {customTags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {customTags.map(tag => (
                <span
                  key={tag}
                  className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-xs font-medium"
                >
                  #{tag}
                  <button onClick={() => setCustomTags(prev => prev.filter(t => t !== tag))}>
                    <X size={11} />
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <input
              type="text"
              value={customTag}
              onChange={e => setCustomTag(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddCustomTag()}
              placeholder="#tag personalizat"
              className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-green-500"
            />
            <button
              onClick={handleAddCustomTag}
              className="px-3 py-2 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700 transition-colors"
            >
              <Plus size={15} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditRecipePostPage;
